import { useStyles } from "@/styles/useStyles";
import { useActiveAssignment } from "@/store/selectors/assignmentSelectors";
import { AssignmentService } from "@/services/AssignmentService";
import { useEffect, useMemo } from "react";
import { Group } from "@mantine/core";
import { Text } from "../ui/Text";
import { IconCircle, IconCircleCheck, IconProgress } from "@tabler/icons-react";
import { IUserPlaylistData } from "@/types/UserDataTypes";
import { useAdvizerMap } from "@/store/selectors";
import { IAssignmentPrompt } from "@/types/AssignmentTypes";

export function useGetAssignmentColor(props: {
  userPlaylistData: IUserPlaylistData;
  userVideoData: any;
  userVideosWatched: any[];
  userResponses: any;
  assignmentPrompts?: Record<string, IAssignmentPrompt>;
}) {
  const {
    userPlaylistData,
    userVideoData,
    userVideosWatched,
    userResponses,
    assignmentPrompts,
  } = props;
  const activeAssignment = useActiveAssignment();
  const advizerMap = useAdvizerMap();
  if (!activeAssignment?.id) return "pink";

  const { status: assignmentResultStatus } =
    AssignmentService.getAssignmentResultStatus({
      assignment: activeAssignment,
      userPlaylistData,
      userVideoData,
      advizerMap,
      userVideosWatched,
      userResponses,
      assignmentPrompts,
    });

  switch (assignmentResultStatus) {
    case "completed":
      return "green";
    case "inProgress":
      return "blue";
    default:
      return "pink";
  }
}
export function UserAssignmentStatus({
  userPlaylistData,
  isLarge = false,
  userVideoData,
  userVideosWatched,
  userResponses,
  assignmentPrompts,
  onAssignmentResultStatusChange,
  resultFetchTrigger,
}: {
  userPlaylistData: IUserPlaylistData;
  isLarge?: boolean;
  userVideoData: any;
  userVideosWatched: any[];
  userResponses: any;
  assignmentPrompts?: Record<string, IAssignmentPrompt>;
  onAssignmentResultStatusChange?: (
    params: ReturnType<typeof AssignmentService.getAssignmentResultStatus>,
  ) => void;
  resultFetchTrigger?: number;
}) {
  const iconSize = isLarge ? 28 : 20;
  const { theme } = useStyles();
  const activeAssignment = useActiveAssignment();
  const advizerMap = useAdvizerMap();
  const { statusText, StatusIcon, statusColor, assignmentResultStatusObject } =
    useMemo(() => {
      if (!activeAssignment?.id)
        return {
          statusText: "Not Started",
          StatusIcon: IconCircle,
          statusColor: theme.colors.pink[6],
          assignmentResultStatusObject: {},
        };

      const assignmentResultStatusObject =
        AssignmentService.getAssignmentResultStatus({
          assignment: activeAssignment,
          userPlaylistData,
          userVideoData,
          advizerMap,
          userVideosWatched,
          userResponses,
          assignmentPrompts,
        });

      const { status: assignmentResultStatus } = assignmentResultStatusObject;

      const configToStatus = {
        completed: {
          statusText: "Completed",
          StatusIcon: IconCircleCheck,
          statusColor: theme.colors.green[6],
        },
        inProgress: {
          statusText: "In Progress",
          StatusIcon: IconProgress,
          statusColor: theme.colors.blue[6],
        },
        notStarted: {
          statusText: "Not Started",
          StatusIcon: IconCircle,
          statusColor: theme.colors.pink[6],
        },
      };

      // if (userAssignmentResult.status === "completed") {
      //   return {
      //     statusText: "Completed",
      //     statusIcon: <IconCheck size={24} />,
      //     statusColor: "green",
      //   };
      // }
      return {
        ...configToStatus[assignmentResultStatus],
        assignmentResultStatusObject,
      };
    }, [
      activeAssignment,
      advizerMap,
      assignmentPrompts,
      theme.colors.blue,
      theme.colors.green,
      theme.colors.pink,
      userPlaylistData,
      userResponses,
      userVideoData,
      userVideosWatched,
    ]);

  useEffect(() => {
    if (!onAssignmentResultStatusChange) return;
    onAssignmentResultStatusChange(
      assignmentResultStatusObject as ReturnType<
        typeof AssignmentService.getAssignmentResultStatus
      >,
    );
  }, [resultFetchTrigger]);

  return (
    <Group gap="xs">
      <StatusIcon size={iconSize} color={statusColor} opacity={0.8} />
      <Text size={isLarge ? "md" : "sm"} fw={400} c={statusColor}>
        {statusText}
      </Text>
    </Group>
  );
}
